import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  useToast,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { IoMdLock } from "react-icons/io";
import { BsUnlockFill } from "react-icons/bs";
import axios from 'axios';
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import Wallets from "./pages/Wallets";
import Friends from "./pages/Friends";
import Profile from "./pages/Profile";
import CoinEarnings from "./pages/CoinEarnings";
import VoiceApproval from "./pages/VoiceApproval";
import Withdrawals from "./pages/Withdrawals";
import CoinPackages from "./pages/CoinPackages";
import Gifts from "./pages/Gifts";
import CallConfig from "./pages/CallConfig";
import CallTransactions from "./pages/CallTransactions";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import ConversionSettings from "./pages/ConversionSettings";
import Analytics from "./pages/Analytics";
import AdvancedReports from "./pages/AdvancedReports";
import AdminDashboard from './pages/AdminDashboard';
import PurchaseHistory from './pages/PurchaseHistory';
import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";
import Rooms from './pages/Rooms';
import UserCoinDetails from './pages/UserCoinDetails';
import UserCallHistory from './pages/UserCallHistory';
import PurchaseHistoryDetails from './pages/PurchaseHistoryDetails';
import PushNotifications from './pages/PushNotifications';
import InAppNotifications from './pages/InAppNotifications';
import Coupons from './pages/Coupons';
import UserActivity from './pages/UserActivity';
import Customisations from './pages/Customisations';
import Leaderboard from './pages/Leaderboard';
import Competitions from './pages/Competitions';
import SuperAdminProtected from './components/utilities/SuperAdminProtected';
import Unauthorized from './pages/Unauthorized';
import ContactUs from './pages/ContactUs';
import Chats from './pages/Chats';
import SpinManager from './pages/SpinManager';
import MoreOptionsConfiguration from './pages/MoreOptionsConfiguration';
import DailyCallRewards from './pages/DailyCallRewards';
import Stories from './pages/Stories';
import { getUserRole } from './components/utilities/getUserRole';
// API configuration

//dev
// const API_URL = 'https://prod-api.youandme.arrowcoders.in';

const API_URL= "https://cockroach-backend.arrowcoders.in";


// const API_URL = 'http://localhost:6060';



// Set up axios defaults
axios.defaults.baseURL = API_URL;
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Axios interceptor to add auth token to requests
axios.interceptors.request.use(
  config => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = React.createContext();
export { AuthContext };

const useAuth = () => {
  return React.useContext(AuthContext);
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const storedUser = localStorage.getItem('adminUser');

    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);


      const response = await axios.post('/api/v1/admin/auth/login', { email, password });

      if (response.data.success) {
        const { token, user } = response.data;
        localStorage.setItem('adminToken', token);
        localStorage.setItem('adminUser', JSON.stringify(user));
        setUser(user);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Login error:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Login failed');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setUser(null);
  };

  const value = {
    user,
    login,
    logout,
    loading,
    error
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" color="pink.400" />
      </Center>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { login, loading, error: authError } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (authError) setError(authError);
  }, [authError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await login(email, password);
    if (success) {
      toast({
        title: "Login successful",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate("/");
    } else {
      toast({
        title: "Login failed",
        description: error || "Invalid email or password",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 sm:p-6"
      style={{
        background:
          "radial-gradient(1200px 800px at 85% -10%, rgba(168, 85, 247, 0.25), transparent 60%)," +
          "radial-gradient(900px 700px at -10% 10%, rgba(236, 72, 153, 0.18), transparent 55%)," +
          "linear-gradient(180deg, #0b0720 0%, #05030f 100%)",
      }}
    >
      <div
        className="w-full max-w-md rounded-3xl overflow-hidden"
        style={{
          background: "linear-gradient(180deg, #17103d 0%, #120c30 100%)",
          border: "1px solid rgba(139, 92, 246, 0.3)",
          boxShadow:
            "0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(168,85,247,0.06), 0 0 80px rgba(168,85,247,0.1)",
          animation: "modalIn 0.35s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        <div
          className="px-8 pt-10 pb-6 text-center"
          style={{
            borderBottom: "1px solid rgba(139, 92, 246, 0.18)",
            background:
              "linear-gradient(180deg, rgba(168,85,247,0.08), transparent)",
          }}
        >
          <div className="flex justify-center mb-5">
            <div className="relative">
              <img
                src="/cockroach-logo.jpg"
                alt="Cockroach"
                className="w-20 h-20 object-cover"
                style={{
                  borderRadius: "24px",
                  border: "1px solid rgba(236, 72, 153, 0.55)",
                  boxShadow: "0 0 40px rgba(168,85,247,0.4)",
                }}
              />
              <span
                className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2"
                style={{
                  background: "#10b981",
                  borderColor: "#120c30",
                  boxShadow: "0 0 10px rgba(16,185,129,0.8)",
                }}
              />
            </div>
          </div>
          <h1
            className="font-bold tracking-tight mb-1"
            style={{
              fontSize: "1.9rem",
              lineHeight: 1.15,
              background: "linear-gradient(135deg, #a855f7 0%, #ec4899 50%, #f43f5e 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Cockroach
          </h1>
          <p
            className="text-sm"
            style={{ color: "#8e83ad", letterSpacing: "0.12em" }}
          >
            ADMIN PANEL
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-8 py-7 space-y-5">
          {error && (
            <div
              className="rounded-xl px-4 py-3 text-sm"
              style={{
                background: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.3)",
                color: "#fca5a5",
              }}
            >
              {error}
            </div>
          )}

          <div>
            <label className="form-label">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email"
              required
              className="input"
              autoComplete="email"
            />
          </div>

          <div>
            <label className="form-label">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                className="input"
                style={{ paddingRight: "2.75rem" }}
                autoComplete="current-password"
              />
              <span
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer select-none"
                style={{ color: "#a855f7" }}
              >
                {showPassword ? (
                  <BsUnlockFill className="text-xl" />
                ) : (
                  <IoMdLock className="text-xl" />
                )}
              </span>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary w-full py-3 text-base"
            disabled={loading}
            style={loading ? { opacity: 0.75, cursor: "progress" } : {}}
          >
            {loading ? (
              <>
                <span
                  className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin"
                  aria-hidden
                />
                <span>Signing in…</span>
              </>
            ) : (
              "Sign In"
            )}
          </button>

          <p
            className="text-center text-xs pt-2"
            style={{ color: "#5e5482" }}
          >
            Protected access · Authorized personnel only
          </p>
        </form>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <div className="min-h-screen" style={{ background: "transparent" }}>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/users" element={<Users />} />
                      <Route path="/coin-earnings" element={<CoinEarnings />} />
                      <Route path="/voice-approval" element={<VoiceApproval />} />
                      <Route path="/withdrawals" element={<Withdrawals />} />
                      <Route path="/coin-packages" element={<CoinPackages />} />
                      <Route path="/conversion-settings" element={<SuperAdminProtected><ConversionSettings /></SuperAdminProtected>
                      }
                      />
                      <Route path="/gifts" element={<Gifts />} />
                      <Route path="/call-config" element={<SuperAdminProtected><CallConfig /></SuperAdminProtected>} />
                      <Route path="/call-transactions" element={<CallTransactions />} />
                      <Route path="/analytics" element={<Analytics />} />
                      <Route path="/reports" element={<Reports />} />
                      <Route path="/advanced-reports" element={<AdvancedReports />} />
                      <Route path="/settings" element={<SuperAdminProtected><Settings /></SuperAdminProtected>} />
                      <Route path="/wallets" element={<Wallets />} />
                      <Route path="/friends" element={<Friends />} />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/purchase-history" element={<PurchaseHistory />} />
                      <Route path="/chats" element={
                        getUserRole()?.email === "admin@arrowcoders.com" 
                          ? <Chats /> 
                          : <Navigate to="/unauthorized" replace />
                      } />
                      <Route path="/admin-managment" element={<SuperAdminProtected><AdminDashboard /></SuperAdminProtected>} />
                      <Route path="/Room-Managment" element={<Rooms />} />
                      <Route path="/call-transactions/:userId" element={<UserCallHistory />} />
                      <Route path="/purchase-history/:userId" element={<PurchaseHistoryDetails />} />
                      <Route path="/unauthorized" element={<Unauthorized />} />
                      <Route path="/coin-earnings/user/:id" element={<UserCoinDetails />} />
                      <Route path="/push-notifications" element={<PushNotifications />} />
                      <Route path="/in-app-notifications" element={<InAppNotifications />} />
                      <Route path="/coupons" element={<Coupons />} />
                      <Route path="/user-activity" element={<UserActivity />} />
                      <Route path="/leaderboard" element={<Leaderboard />} />
                      <Route path="/leaderboard/competitions" element={<Competitions />} />
                      <Route path="/customisations" element={<SuperAdminProtected><Customisations /></SuperAdminProtected>} />
                      <Route path="/spin-manager" element={<SuperAdminProtected><SpinManager /></SuperAdminProtected>} />
                      <Route path="/contact-us" element={<ContactUs />} />
                      <Route path="/more-options" element={<MoreOptionsConfiguration />} />
                      <Route path="/daily-call-rewards" element={<DailyCallRewards />} />
                      <Route path="/stories" element={<Stories />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </div>
  );
}
export default App;
